import _ from 'lodash'
import {fetch, post, put, remove} from '@/base-http';
import Permission from '@/modules/authorization/model/Permission'
import Role from '@/modules/authorization/model/Role';
import Pagination, {PaginationInput} from '@/modules/core/model/pagination';
import RoleUserAssignation from '@/modules/authorization/model/RoleUserAssignation';
import KeyValuePair from '@/modules/core/support/KeyValuePair';
import {CheckAuthorizationResult} from '@/modules/authorization/model/CheckAuthorizationResult';

/**
 * Obtiene un permiso por su ruta
 *
 * @param path Ruta completamente cualificada del permiso
 */
export async function getPermission (path: string) {
    const data = await fetch(`/authorization/permission/${path}`)

    return Permission.deserialize(data)
}

/**
 * Registra en el servidor un nuevo permiso
 *
 * @param permission
 */
export async function createPermission (permission: Permission) {
    try {
        const result = await post('/authorization/permission', permission.serialize())

        return Permission.deserialize(result)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Actualiza un permiso existente
 *
 * @param permission
 */
export async function updatePermission (permission: Permission) {
    try {
        const result = await put(
            `/authorization/permission/${permission.path}`,
            permission.serialize(),
        )

        return Permission.deserialize(result)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Obtiene un rol de usuario registrado en la aplicación en base a su
 * ruta
 *
 * @param path
 */
export async function getRole (path: string) {
    const data = await fetch(`/authorization/roles/${path}`)

    return Role.deserialize(data)
}

/**
 * Registra un nuevo rol en la aplicación
 *
 * @param role
 */
export async function createRole (role: Role) {
    if (!role.isNew) {
        throw new Error(`El con id ${role.id} ya se encuentra registrado en la aplicación`)
    }

    const newRole = await post('/authorization/role', role.serialize())

    return Role.deserialize(newRole)
}

/**
 * Actualiza un rol de usuario en el servidor
 *
 * @param role
 */
export async function updateRole (role: Role) {
    if (role.isNew) {
        throw new Error('No se puede actualizar un rol que aún no ha sido registrado')
    }

    try {
        const data = await put(`/authorization/role/${role.path}`, role.serialize())

        return Role.deserialize(data)
    } catch (error) {
        throw new Error(_.get(error, 'response.data.message', error.message))
    }
}

/**
 * Eliminar rol de usuario
 *
 * @param path
 */
export async function deleteRole (path: string) {
    try {
        const result = await remove(`/authorization/role/${path}`)

        return result.success
    } catch (error) {
        throw new Error(_.get(error, 'response.data.message', error.message))
    }
}

export interface UserAssignationListFilters {
    search?: string
    allUsers?: boolean
}

export interface RoleUserAssignationListInput
    extends PaginationInput <UserAssignationListFilters > {}

/**
 * Obtiene del servidor la lista de usuario asignados a un rol
 *
 * @param role
 * @param input
 */
export async function getRoleUserAssignationList (role: string, input: RoleUserAssignationListInput) {
    const result = await fetch('/authorization/role_user_assignation_list', {
        params: {
            role,
            allUsers: _.get(input, 'filters.allUsers', false) ? '1' : '0',
            search: _.get(input, 'filters.search', ''),
            page: input.page || 1,
            recordsPerPage: input.recordsPerPage || 50,
            orderBy: _.get(input, 'orderBy.field', 'id'),
            orderByDirection: _.get(input, 'orderBy.direction', 'desc'),
        },
    })

    return {
        totalRecords: result.total_records || 0,
        totalPages: 5,
        items: result.records.map((record: KeyValuePair) => RoleUserAssignation.deserialize(record)),
    } as Pagination<RoleUserAssignation>
}

/**
 * Actualiza la lista de usuarios asignados
 */
export async function updateRoleUserAssignationList (role: string, assignations: RoleUserAssignation[]) {
    const result = await put('/authorization/role_user_assignation_list', {
        role,
        users: assignations.map(a => ({
            id: a.userId,
            has_role: a.hasRole,
        })),
    })

    return result.sucess || false
}

/**
 * Comprueba si el usuario actual posee los permisos argumentados
 *
 * @param permissions
 */
export async function checkAuthorization (permissions: string[]) {
    const params: KeyValuePair = {}
    permissions.forEach((permission, index) => {
        params[`permission_${ index + 1 }`] = permission
    })

    const result = await fetch('/authorization/status', {
        params,
    })

    return CheckAuthorizationResult.deserialize(result)
}
